/**
 * Date utility functions for Matches feature
 */

/**
 * Format date for display
 * @param {string} isoString - ISO date string
 * @param {object} options - Intl.DateTimeFormat options
 */
export function formatDateTime(isoString, options = {}) {
  if (!isoString) return '-';
  const d = new Date(isoString);
  return d.toLocaleString('nl-NL', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...options,
  });
}

/**
 * Format date only (no time)
 */
export function formatDate(isoString) {
  if (!isoString) return '-';
  const d = new Date(isoString);
  return d.toLocaleDateString('nl-NL', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * Format time only
 */
export function formatTime(isoString) {
  if (!isoString) return '-';
  const d = new Date(isoString);
  return d.toLocaleTimeString('nl-NL', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Check if a match is on a given date
 */
export function isMatchOnDate(match, date) {
  if (!match.start_time) return false;
  const matchDate = new Date(match.start_time);
  return (
    matchDate.getFullYear() === date.getFullYear() &&
    matchDate.getMonth() === date.getMonth() &&
    matchDate.getDate() === date.getDate()
  );
}

/**
 * Get match status based on current time
 */
export function getMatchStatus(match) {
  if (!match.start_time) return 'unknown';
  const now = new Date();
  const start = new Date(match.start_time);
  const end = match.end_time ? new Date(match.end_time) : new Date(start.getTime() + 2 * 60 * 60 * 1000); // Default 2 hours

  if (now < start) return 'upcoming';
  if (now >= start && now <= end) return 'live';
  return 'past';
}

/**
 * Check if date is today
 */
export function isToday(date) {
  const today = new Date();
  return (
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate()
  );
}

/**
 * Check if two dates are the same day
 */
export function isSameDay(date1, date2) {
  if (!date1 || !date2) return false;
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

/**
 * Convert an ISO datetime string to a value suitable for datetime-local inputs.
 * Converts UTC to the browser's local timezone so the displayed time matches
 * what the user originally entered.
 */
export function toLocalDateTimeInput(isoString) {
  if (!isoString) return '';
  const d = new Date(isoString);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

/**
 * Shift a `YYYY-MM-DDTHH:mm` datetime-local value by whole hours, returning the
 * same format. Goes via Date so month ends and DST transitions are handled
 * rather than string-patched.
 *
 * Returns '' for an empty or unparseable input, which is what the date inputs
 * want for "leave it alone".
 */
export function shiftLocalDateTimeInput(value, hours) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  d.setHours(d.getHours() + hours);
  return toLocalDateTimeInput(d.toISOString());
}

/**
 * Get relative time description
 */
export function getRelativeTime(isoString) {
  if (!isoString) return '';
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = date - now;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins > 0 && diffMins < 60) return `in ${diffMins} min`;
  if (diffHours > 0 && diffHours < 24) return `in ${diffHours}h`;
  if (diffDays > 0 && diffDays < 7) return `in ${diffDays}d`;
  if (diffMins < 0 && diffMins > -60) return `${-diffMins} min ago`;
  if (diffHours < 0 && diffHours > -24) return `${-diffHours}h ago`;
  if (diffDays < 0 && diffDays > -7) return `${-diffDays}d ago`;
  return formatDate(isoString);
}
